import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/client";
import type { ClusterSigningMode, ClusterType, DocumentationCluster } from "../types";

const BASE = "/project-docs/clusters";

export function useDocumentationClusters(projectId?: string, signingMode?: ClusterSigningMode | "") {
  return useQuery({
    queryKey: ["doc-clusters", projectId, signingMode],
    queryFn: () =>
      apiClient
        .get<{ data: { results: DocumentationCluster[] } }>(BASE + "/", {
          params: {
            ...(projectId ? { project: projectId } : {}),
            ...(signingMode ? { signing_mode: signingMode } : {}),
          },
        })
        .then((r) => r.data.data.results),
    enabled: true,
    staleTime: 30_000,
  });
}

export function useDocumentationCluster(id: string) {
  return useQuery({
    queryKey: ["doc-cluster", id],
    queryFn: () =>
      apiClient
        .get<{ data: DocumentationCluster }>(`${BASE}/${id}/`)
        .then((r) => r.data.data),
    staleTime: 30_000,
  });
}

interface CreateClusterPayload {
  project: string;
  cluster_type?: ClusterType;
  contractor?: string | null;
  employee_signer?: string | null;
  employee_agreement?: string | null;
  signature_type?: string | null;
  reviewing_director?: string | null;
  name: string;
  description?: string;
  signing_mode?: ClusterSigningMode;
}

export interface DirectorBrief {
  id: string;
  full_name: string;
}

export function useDirectorsByBranch() {
  return useQuery({
    queryKey: ["directors-by-branch"],
    queryFn: () =>
      apiClient
        .get<{ data: DirectorBrief[] }>("/auth/directors/")
        .then((r) => r.data.data),
  });
}

/** Fields a director/CEO may change after creation — name, cluster type and signer. */
export interface UpdateClusterPayload {
  name?: string;
  cluster_type?: ClusterType;
  contractor?: string | null;
  employee_signer?: string | null;
  employee_agreement?: string | null;
  signature_type?: string | null;
  reviewing_director?: string | null;
}

export function useUpdateCluster(clusterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateClusterPayload) =>
      apiClient
        .patch<{ data: DocumentationCluster }>(`${BASE}/${clusterId}/`, payload)
        .then((r) => r.data.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["doc-cluster", clusterId] });
      qc.invalidateQueries({ queryKey: ["doc-clusters"] });
    },
  });
}

export function useDeleteCluster() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (clusterId: string) =>
      apiClient.delete(`${BASE}/${clusterId}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["doc-clusters"] });
    },
  });
}

export function useCreateCluster() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateClusterPayload) =>
      apiClient
        .post<{ data: DocumentationCluster }>(BASE + "/", payload)
        .then((r) => r.data.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["doc-clusters"] });
    },
  });
}

/** Internal employees selectable as cluster members. Shape matches `/auth/users/?selectable=true`. */
export interface InternalUserOption {
  id: string;
  username: string;
  first_name: string;
  last_name: string;
  role: string;
}

export function useSelectableInternalUsers(enabled: boolean) {
  return useQuery({
    queryKey: ["doc-cluster-selectable-users"],
    queryFn: () =>
      apiClient
        // Returns a flat array (not a paginated envelope); already excludes
        // contractors and inactive/hidden accounts.
        .get<{ data: InternalUserOption[] }>("/auth/users/?selectable=true")
        .then((r) => r.data.data ?? []),
    enabled,
  });
}

/** Both member mutations return the updated cluster, so detail views refresh from the response. */
export function useAddClusterMember(clusterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      apiClient
        .post<{ data: DocumentationCluster }>(`${BASE}/${clusterId}/members/`, {
          user_id: userId,
        })
        .then((r) => r.data.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["doc-cluster", clusterId] });
      qc.invalidateQueries({ queryKey: ["doc-clusters"] });
    },
  });
}

export function useRemoveClusterMember(clusterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      apiClient
        .delete<{ data: DocumentationCluster }>(
          `${BASE}/${clusterId}/members/${userId}/`,
        )
        .then((r) => r.data.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["doc-cluster", clusterId] });
      qc.invalidateQueries({ queryKey: ["doc-clusters"] });
    },
  });
}

export function useDocumentationPendingCount() {
  return useQuery({
    queryKey: ["doc-pending-count"],
    queryFn: () =>
      apiClient
        .get<{ data: { count: number } }>("/project-docs/pending-count/")
        .then((r) => r.data.data.count),
  });
}

export function useDocumentationAwaitingCount() {
  return useQuery({
    queryKey: ["doc-awaiting-count"],
    queryFn: () =>
      apiClient
        .get<{ data: { count: number } }>("/project-docs/awaiting-count/")
        .then((r) => r.data.data.count),
  });
}

export function useDocumentationUnacknowledgedCount() {
  return useQuery({
    queryKey: ["doc-unacknowledged-count"],
    queryFn: () =>
      apiClient
        .get<{ data: { count: number } }>("/project-docs/unacknowledged-count/")
        .then((r) => r.data.data.count),
  });
}

interface CreateBillingOrderPayload {
  framework_agreement_id: string;
  line_item_type_id: string;
  cluster_ids: string[];
}

export interface CreatedBillingOrder {
  id: string;
  order_number: string;
  project_id: string;
  project_name: string;
  cluster_count: number;
}

export function useCreateBillingOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateBillingOrderPayload) =>
      apiClient
        .post<{ data: { orders: CreatedBillingOrder[]; count: number } }>(
          "/project-docs/create-billing-order/",
          payload,
        )
        .then((r) => r.data.data.orders),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["doc-clusters"] });
    },
  });
}

interface MarkSettledExternallyPayload {
  cluster_ids: string[];
  note?: string;
}

export function useMarkSettledExternally() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: MarkSettledExternallyPayload) =>
      apiClient
        .post<{ data: { settled_ids: string[]; count: number } }>(
          "/project-docs/mark-settled-externally/",
          payload,
        )
        .then((r) => r.data.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["doc-clusters"] });
    },
  });
}

interface UnsettleClustersPayload {
  cluster_ids: string[];
}

export function useUnsettleClusters() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UnsettleClustersPayload) =>
      apiClient
        .post<{ data: { unsettled_ids: string[]; count: number } }>(
          "/project-docs/unsettle/",
          payload,
        )
        .then((r) => r.data.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["doc-clusters"] });
      qc.invalidateQueries({ queryKey: ["doc-cluster"] });
    },
  });
}
